import type {
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

export const callsSystemWorkspace:
  SystemWorkspaceDefinition = {
    id: 'calls',
    name: 'Calls',
    description:
      'Governs call creation, incoming delivery, ringing, acceptance, decline, timeout, media connection, ending, notification delivery, recovery, and post-call routing.',
    owningGovernor:
      'Calls Governor',
    authoritySummary:
      'CallsLifecycleAuthority is the single call_sessions mutation owner. Live certification additionally requires native delivery, notification, WebRTC, routing, and correlated device evidence.',
    health: 'degraded',

    statusSignals: {
      criticalFailure: false,
      simulationRunning: false,
      repairRunning: false,
      currentEvidenceAvailable: true,
      nonCriticalIssue: true,
      reason:
        'Lifecycle ownership is centralized, but foreground, background, app-closed delivery, answer connection, media, and recovery still require live certification.',
    },

    authoritySources: [
      {
        id: 'calls-lifecycle-authority',
        kind: 'runtime_engine',
        label:
          'Calls lifecycle mutation authority',
        reference:
          'src/governor/calls/CallsLifecycleAuthority.ts',
        authoritative: true,
      },
      {
        id: 'call-sessions',
        kind: 'database_table',
        label:
          'Persisted call lifecycle',
        reference:
          'public.call_sessions',
        authoritative: true,
      },
      {
        id: 'native-call-page',
        kind: 'frontend',
        label:
          'Native caller route',
        reference:
          'src/pages/NativeCallPage.tsx',
        authoritative: false,
      },
      {
        id: 'call-modal',
        kind: 'frontend',
        label:
          'WebRTC media runtime',
        reference:
          'src/components/CallModal.tsx',
        authoritative: true,
      },
      {
        id: 'incoming-detector',
        kind: 'runtime_engine',
        label:
          'Incoming call detector',
        reference:
          'src/components/AppLayout.tsx',
        authoritative: true,
      },
      {
        id: 'calls-observation',
        kind: 'runtime_engine',
        label:
          'Calls runtime observation bridge',
        reference:
          'src/governor/calls/CallsRuntimeObservationBridge.ts',
        authoritative: true,
      },
      {
        id: 'calls-live-device-gate',
        kind: 'runtime_engine',
        label:
          'Calls live-device certification authority',
        reference:
          'src/governor/calls/CallsLiveDeviceCertificationEngine.ts',
        authoritative: true,
      },
    ],

    dependencies: [
      {
        systemId: 'authentication',
        relationship: 'required',
        reason:
          'Caller and receiver identities must be authenticated.',
      },
      {
        systemId: 'database',
        relationship: 'required',
        reason:
          'Call lifecycle state and evidence must persist.',
      },
      {
        systemId: 'notifications',
        relationship: 'required',
        reason:
          'Incoming calls must reach foreground, background, and closed-app receivers.',
      },
      {
        systemId: 'native-runtime',
        relationship: 'required',
        reason:
          'Android notification, wake, audio, and action handling are native responsibilities.',
      },
      {
        systemId: 'messaging',
        relationship: 'shared',
        reason:
          'Family messaging surfaces initiate and receive Calls UI actions.',
      },
    ],

    certification: {
      source: 'pass',
      build: 'pass',
      simulatedRuntime: 'not_run',
      deployedRuntime: 'pending',
      liveTransaction: 'fail',
      recovery: 'pending',
    },

    simulatorPolicy: {
      enabled: true,
      productionWritesAllowed: false,
      requiresExplicitLiveApproval: true,
      evidenceNamespace:
        'simulated:calls',
      supportedModes: [
        'SIMULATED',
        'REPLAYED',
        'HISTORICAL',
      ],
      resetRequired: true,
    },

    aiPolicy: {
      enabled: true,
      maximumAutonomousLevel: 3,
      allowedLevels: [0, 1, 2, 3],
      mayReadEvidence: true,
      mayGenerateScenarios: true,
      mayRecommendRepairs: true,
      mayPrepareChanges: true,
      mayExecuteProductionWrites: false,
      mustCiteEvidence: true,
      constitutionOverridesRecommendations: true,
    },

    milestone: {
      current:
        'CC-CALLS-002B — High-priority FCM call delivery authority',
      completed: [
        'CC-CALLS-000A',
        'CC-CALLS-000B',
        'CC-CALLS-000C1',
        'CC-CALLS-000C2',
        'CC-CALLS-001A',
        'CC-CALLS-001B',
        'CC-CALLS-001C1',
        'CC-CALLS-001C2A',
        'CC-CALLS-001C2E',
        'CC-CALLS-001C2F',
        'CC-CALLS-001C2G',
        'CC-CALLS-001C2H',
        'CC-CALLS-002A',
        'CC-CALLS-002B',
        'CG-031',
        'CG-032',
        'CG-033',
        'CG-034',
        'CG-035',
      ],
      pending: [
        'CC-CALLS-001C2B — Execute unified foreground two-device certification',
        'CC-CALLS-001D — Background and app-closed delivery',
        'CC-CALLS-001E — Answer and media connection',
        'CC-CALLS-001F — End-route and recovery certification',
      ],
    },
  };
